import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Grid, Container, Button, IconButton, Typography } from "@mui/material";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import CameraIcon from "../../assets/Icon/CameraIcon.png";
import { useLocation, useNavigate } from "react-router-dom";
import CustomButton from "../../components/Custom/CustomButton";
import useFormValidation from "../../components/Custom/FormValidation";
import { createCropValidationSchema } from "../../components/Custom/ValidationSchema";
import { useAlert } from "../../components/Alert";
import CustomTextField from "../../components/Custom/CustomTextField";
import { CropFields, PlantationFields } from "../../Data/fieldData";
import { compressImage } from "../../globals/utils";
import LoaderContainer from "../../components/LoaderContainer";
import { logoutToParent } from "../../globals/authentication";
import CustomToggle from "../../components/Custom/CustomToggle";
import { getUserBasicDetailFromAuth } from "../../utils/common";
const cropValidationSchema = createCropValidationSchema();

const CreateCrop = () => {
  const { is_internal_login } = getUserBasicDetailFromAuth();
  const [farms, setFarms] = useState([]);
  const [formData, setFormData] = useState({
    input_method: "SEED",
    sowing_source: "SEED",
    is_ebc_contest: false,
    is_ebc_applied: false,
    ebc_quantity: 0,
  });
  const { t } = useTranslation();
  const [selectedFileName, setSelectedFileName] = useState("");
  const { errors, validate } = useFormValidation(
    formData,
    cropValidationSchema
  );
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const cropId = searchParams.get("cropId");
  const [isEdit, setIsEdit] = useState(false);
  const { showAlert } = useAlert();
  const [commodity, setCommodity] = useState([]);
  const [species, setSpecies] = useState([]);
  const [farmType, setFarmType] = useState("CROP");
  const [fieldList, setFieldList] = useState();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const farmId = searchParams.get("farmId");
  useEffect(() => {
    const fetchData = async () => {
      if (cropId) {
        await fetchCropData(cropId);
        setIsEdit(true);
      }

      await api.get("/farmer/farms/").then((res) => {
        const _farms = res.data.map((f) => {
          return { id: f.id, farm_name: f.farm_name };
        });
        setFarms(_farms);

        if (farmId) {
          const selectedFarm = _farms.find(
            (farm) => farm.id === parseInt(farmId, 10)
          );
          if (selectedFarm) {
            setFormData((prevData) => ({
              ...prevData,
              farmland: selectedFarm.id,
            }));
          }
        }
      });

      await fetchCropsList();
      setLoading(false);
    };

    fetchData();
  }, [cropId, farmId]);

  useEffect(() => {
    if (!loading) {
      setFieldList(
        farmType === "CROP"
          ? CropFields(t, farms, commodity)
          : PlantationFields(t, farms, species)
      );
      setFormData((prevData) => ({ ...prevData, farm_type: farmType }));
    }
  }, [farmType, farms, commodity, species, loading]);

  const fetchCropsList = async () => {
    try {
      const response = await api.get("/common/commodities/");
      let _commodities = [];
      let _species = [];
      response.data.forEach((detail) => {
        if (detail?.type == "PLANTATION") {
          _species.push(detail);
        } else {
          _commodities.push(detail);
        }
      });
      setCommodity(_commodities);
      setSpecies(_species);
    } catch (error) {
      console.log("Error Fetching Commodities list:", error);
    }
  };

  const fetchCropData = async (cropId) => {
    api
      .get(`/farmer/crops/${cropId}/`)
      .then((response) => {
        if (response?.data) {
          const cropData = response.data;
          const {
            commodity,
            estimated_harvest,
            seed_quantity,
            sapling_quantity,
            seed_type,
            sowing_date,
            seed_source,
            estimated_harvest_date,
            program,
            farmland,
            nursery_name,
            nursery_location,
            plantation_method,
            pit_size,
            plant_count,
            farm_type,
            sowing_source,
            is_ratoon,
            is_ebc_contest,
            ebc_quantity,
          } = cropData;
          setFormData({
            commodity: commodity ? commodity.id : "",
            estimated_harvest,
            seed_quantity,
            sapling_quantity,
            seed_type,
            sowing_date,
            seed_source,
            estimated_harvest_date,
            program: program ? program.id : "",
            farmland: farmland ? farmland.id : "",
            nursery_name,
            nursery_location,
            plantation_method,
            pit_size,
            plant_count,
            farm_type,
            is_ratoon,
            sowing_source,
            is_ebc_contest,
            is_ebc_applied: ebc_quantity > 0,
            ebc_quantity,
          });
          setFarmType(farm_type); // Set the farm type based on the response
        }
      })
      .catch((error) => {
        console.error("Error fetching farm data:", error);
      });
  };

  const handleChange = (name, value) => {
    if (name === "is_ebc_contest") {
      if (!value) {
        setFormData((prevData) => ({
          ...prevData,
          is_ebc_applied: false,
          ebc_quantity: "",
        }));
      }
    }
    if (name === "is_ebc_applied") {
      if (!value) {
        setFormData((prevData) => ({
          ...prevData,
          ebc_quantity: "",
        }));
      }
    }
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    validate({ ...formData, [name]: value });
  };

  const onFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    try {
      const compressedFile = await compressImage(file);
      setFormData((prevData) => ({
        ...prevData,
        [event.target.name]: compressedFile,
      }));
    } catch (error) {
      console.error("Error compressing image:", error);
      showAlert(
        "error",
        "An error occurred while compressing the image. Please try again."
      );
    }
    setSelectedFileName(file.name);
  };

  const createCrop = async () => {
    try {
      // TODO: Fix the validation for crop and plantation separately
      // validate(formData);

      if (Object.keys(errors).length === 0) {
        setLoading(true);
        const fileFields = ["sowing_file"];
        const _formData = new FormData();

        for (const key in formData) {
          if (
            (fileFields.includes(key) && typeof formData[key] === "string") ||
            !formData[key]
          )
            continue;
          _formData.append(key, formData[key]);
        }
        if (!_formData.is_ebc_applied && _formData.ebc_quantity) {
          _formData.append("ebc_quantity", 0);
        }
        delete _formData.is_ebc_applied;

        const apiRequest = isEdit
          ? api.patch(`/farmer/crops/${cropId}/`, _formData)
          : api.post("/farmer/crops/", _formData);

        const response = await apiRequest;
        console.log("Response:", response);
        showAlert(
          "success",
          isEdit ? "Updated Successfully" : "Created Successfully"
        );
        // When the crop is created for a new farm, redirect
        // to subscription invoicing page as part of wizard flow
        if (!isEdit && farmId && is_internal_login) {
          logoutToParent("/subscription-invoicing?farm_id=" + farmId);
        } else {
          navigate("/");
        }
      } else {
        showAlert(
          "error",
          "Please fix the validation errors before submitting."
        );
        console.error("Validation errors:", errors);
      }
    } catch (error) {
      console.error(error);
      showAlert("error", "Error adding crop");
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Helmet>
        <title>Add Planting - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <Container maxWidth="xs">
        <LoaderContainer loading={loading}>
          <div>
            <Grid
              container
              justify="center"
              direction="column"
              style={{ marginTop: "10px" }}
            >
              <CustomTitle>{t("Add Planting")}</CustomTitle>
              <>
                {/* <ToggleButtonGroup
                  value={farmType}
                  exclusive
                  onChange={(event, newFarmType) => {
                    if (newFarmType !== null) {
                      setFarmType(newFarmType);
                    }
                  }}
                  style={{ width: "100%" }}
                >
                  <ToggleButton
                    value="CROP"
                    style={{ flex: 1, height: "33px" }}
                  >
                    {t("Crop")}
                  </ToggleButton>
                  <ToggleButton
                    value="PLANTATION"
                    style={{ flex: 1, height: "33px" }}
                  >
                    {t("Plantation")}
                  </ToggleButton>
                </ToggleButtonGroup> */}
                {fieldList &&
                  fieldList.map((field) => {
                    let dynamicLabel = field.label;

                    if (field.name === "sowing_date") {
                      dynamicLabel = formData.is_ratoon
                        ? t("Start Date")
                        : t("Sowing Date");
                    }
                    if (field.name === "estimated_harvest") {
                      const selectedcommodity = commodity.find(
                        (c) => c.id === formData.commodity
                      );
                      dynamicLabel = `${t("Estimated Harvested Amount")} (${
                        selectedcommodity?.name === "Coconut" ? "Nuts" : "Kg"
                      })`;
                    }

                    if (
                      field.name === "seed_quantity" &&
                      formData.sowing_source === "SAPLING"
                    ) {
                      return null;
                    }

                    if (
                      field.name === "sapling_quantity" &&
                      formData.sowing_source === "SEED"
                    ) {
                      return null;
                    }

                    return (
                      <CustomTextField
                        key={field.name}
                        label={dynamicLabel}
                        name={field.name}
                        value={formData[field.name] || ""}
                        onChange={handleChange}
                        type={field.type}
                        options={field.options}
                        error={errors[field.name]}
                        helperText={errors[field.name]}
                        required={field.required}
                      />
                    );
                  })}

                <CustomToggle
                  name="is_ebc_contest"
                  label={t("Are you interested in ASQI EBC contest?")}
                  isChecked={formData.is_ebc_contest || false}
                  handleOnChange={handleChange}
                  containerStyles={{
                    marginTop: "8px",
                  }}
                />
                {formData.is_ebc_contest && (
                  <CustomToggle
                    name="is_ebc_applied"
                    label={t("Have you added for ASQI EBC to this crop?")}
                    isChecked={formData.is_ebc_applied || false}
                    handleOnChange={handleChange}
                    containerStyles={{
                      marginTop: "8px",
                    }}
                  />
                )}
                {formData.is_ebc_applied && (
                  <CustomTextField
                    label={t("Applied EBC Quantity (kg)")}
                    name="ebc_quantity"
                    value={formData.ebc_quantity || ""}
                    onChange={handleChange}
                    type="number"
                    error={errors.ebc_quantity}
                    helperText={errors.ebc_quantity}
                    required={false}
                    disabled={!formData.is_ebc_applied}
                  />
                )}

                <Grid container sx={{ marginTop: "10px" }}>
                  <Grid item xs={5}>
                    <Typography style={{ fontSize: "12px", fontWeight: 600 }}>
                      {t("Add Farmsite Picture")}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton sx={{ padding: "0px" }}>
                      <img
                        alt="camera-icon"
                        src={CameraIcon}
                        style={{ width: "50px", height: "47px" }}
                      />
                    </IconButton>
                  </Grid>
                  <Grid item xs={5}>
                    <input
                      type="file"
                      id="sowing-file"
                      name="sowing_file"
                      accept="image/*"
                      onChange={onFileChange}
                      style={{ display: "none" }}
                    />
                    <Button
                      variant="contained"
                      component="label"
                      style={{
                        marginLeft: "auto",
                        color: "white",
                        width: "100%",
                        height: "33px",
                      }}
                      htmlFor="sowing-file"
                    >
                      {t("Upload")}
                    </Button>
                  </Grid>
                </Grid>
                <span
                  style={{
                    marginTop: "-20px",
                    fontSize: "10px",
                    fontWeight: 400,
                  }}
                >
                  ( {t("optional")})
                </span>
                {selectedFileName && (
                  <Typography>{selectedFileName}</Typography>
                )}
                <CustomButton onClick={createCrop}>
                  {isEdit ? t("Save") : t("Create Crop")}
                </CustomButton>
              </>
            </Grid>
          </div>
        </LoaderContainer>
      </Container>
    </>
  );
};

export default CreateCrop;
